import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import AlwaysOnSummary from './AlwaysOnSummary'
import OfferBNPLAd from '../OfferBNPLAd'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOptionWithDefault, getTacticalPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import ContactUSP from '../ContactUSP'
import AlwaysOnPropertyDetails from './AlwaysOnPropertyDetails'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { useAppSelector } from 'hooks/reduxHooks'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content 1fr;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const Summary = styled(AlwaysOnSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-row: 1;
    grid-column: 2;
  }
`

const MobileBNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  order: 1;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  promotions: Array<App.OfferPackageOptionView>
  cheapestOption: App.OfferPackageOptionView
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  shouldShowSalesPhoneUSP: boolean
  shareConfig: ShareConfig
}

function AlwaysOnIntro(props: Props) {
  const {
    offer,
    promotions,
    onViewPackageOptions,
    shouldShowSalesPhoneUSP,
    shareConfig,
    cheapestOption,
  } = props

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldPromo = isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
    offer,
    currency,
    offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
    cheapestOption.package.price,
  )

  return (
    <OfferIntroWrapper>
      <VerticalSpacer gap={16}>
        <OfferIntroGrid data-testid="offer-intro">
          <OfferPageLabelGroup offer={offer} />
          <OfferOtherActions>
            <TripGuard inverse>
              <ShareModal shareConfig={shareConfig} openOnHash="#share">
                <ShareWithFriendsButton horizontalOutdent="end" />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <ShareModal shareConfig={shareConfig} openOnHash="#share">
                <ShareWithFriendsButton />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </TripGuard>
          </OfferOtherActions>
          <AlwaysOnPropertyDetails
            offer={offer}
            promotions={promotions}
          />
          {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
            <CSSBreakpoint max="mobile">
              <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
            </CSSBreakpoint>
          </TasteOfGoldPromoCard>}
          <SummaryBlock>
            <div>
              <Summary onViewPackageOptions={onViewPackageOptions} />
            </div>
            <OfferBNPLAd prices={allPrices} />
          </SummaryBlock>
          <MobileBNPLAd prices={allPrices} />
        </OfferIntroGrid>
        {(shouldShowSalesPhoneUSP || promotions.length > 0) && <ContactUSP />}
      </VerticalSpacer>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 300),
  open: !!state.activeFilter,
}), AlwaysOnIntro, preventRenderWithOpenFilter)
